import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import ApiService from '../../services/ApiService';
import ProfilePic from '../profilepic/ProfilePic';
import premiumBadge from '../../assets/premium_badge.svg';
import './AccountHoverCard.css';

const API_DOMAIN = 'https://api.whitewhale.help';
const SHOW_DELAY = 280;
const HIDE_DELAY = 140;
const profileCache = new Map();

const getCacheKey = (account) => account?.id
    ? `id:${account.id}`
    : account?.nickname
        ? `nickname:${account.nickname}`
        : null;

const loadProfile = (account) => {
    const cacheKey = getCacheKey(account);
    if (!cacheKey) return Promise.resolve(account);
    if (profileCache.has(cacheKey)) return profileCache.get(cacheKey);

    const request = (account.id
        ? ApiService.getAccountById(account.id)
        : ApiService.get(account.nickname))
        .then((response) => ({ ...account, ...(response?.data || {}) }))
        .catch(() => account);

    profileCache.set(cacheKey, request);
    return request;
};

const getPresence = (lastWatchedTime) => {
    if (!lastWatchedTime) return { online: false, label: 'Статус не указан' };

    const lastSeen = new Date(lastWatchedTime);
    if (Number.isNaN(lastSeen.getTime())) return { online: false, label: 'Статус не указан' };

    const difference = Math.max(0, Date.now() - lastSeen.getTime());
    const minutes = Math.floor(difference / 60000);
    if (minutes <= 5) return { online: true, label: 'Сейчас онлайн' };
    if (minutes < 60) return { online: false, label: `${minutes} мин. назад` };

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return { online: false, label: `${hours} ч. назад` };

    return {
        online: false,
        label: lastSeen.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' }),
    };
};

const formatRegistrationDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('ru-RU', { month: 'short', year: 'numeric' });
};

const AccountHoverCard = ({ account, children }) => {
    const triggerRef = useRef(null);
    const showTimer = useRef(null);
    const hideTimer = useRef(null);
    const tooltipId = useId();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [profile, setProfile] = useState(account || {});
    const [position, setPosition] = useState({ top: 0, left: 0, placement: 'right' });
    const [backgroundFailed, setBackgroundFailed] = useState(false);

    const canShow = Boolean(account && (account.id || account.nickname) && !account.banned);

    const clearTimers = useCallback(() => {
        window.clearTimeout(showTimer.current);
        window.clearTimeout(hideTimer.current);
    }, []);

    const updatePosition = useCallback(() => {
        if (!triggerRef.current) return;

        const rect = triggerRef.current.getBoundingClientRect();
        const viewportPadding = 12;
        const gap = 14;
        const cardWidth = Math.min(340, window.innerWidth - viewportPadding * 2);
        const estimatedHeight = 304;
        let placement = 'right';
        let left = rect.right + gap;

        if (left + cardWidth > window.innerWidth - viewportPadding) {
            left = rect.left - cardWidth - gap;
            placement = 'left';
        }
        if (left < viewportPadding) {
            left = Math.min(
                Math.max(viewportPadding, rect.left + (rect.width / 2) - (cardWidth / 2)),
                window.innerWidth - cardWidth - viewportPadding,
            );
            placement = 'center';
        }

        const top = Math.min(
            Math.max(viewportPadding, rect.top + (rect.height / 2) - 72),
            Math.max(viewportPadding, window.innerHeight - estimatedHeight - viewportPadding),
        );

        setPosition({ top, left, placement });
    }, []);

    const openCard = useCallback(() => {
        if (!canShow) return;
        window.clearTimeout(hideTimer.current);
        window.clearTimeout(showTimer.current);
        showTimer.current = window.setTimeout(() => {
            updatePosition();
            setIsOpen(true);
        }, SHOW_DELAY);
    }, [canShow, updatePosition]);

    const closeCard = useCallback(() => {
        window.clearTimeout(showTimer.current);
        window.clearTimeout(hideTimer.current);
        hideTimer.current = window.setTimeout(() => setIsOpen(false), HIDE_DELAY);
    }, []);

    useEffect(() => {
        setProfile(account || {});
        setBackgroundFailed(false);
    }, [account]);

    useEffect(() => {
        if (!isOpen || !canShow) return undefined;

        let active = true;
        setIsLoading(true);
        loadProfile(account).then((loadedProfile) => {
            if (!active) return;
            setProfile(loadedProfile || account);
            setIsLoading(false);
        });

        const reposition = () => updatePosition();
        window.addEventListener('resize', reposition);
        window.addEventListener('scroll', reposition, true);
        return () => {
            active = false;
            window.removeEventListener('resize', reposition);
            window.removeEventListener('scroll', reposition, true);
        };
    }, [account, canShow, isOpen, updatePosition]);

    useEffect(() => () => clearTimers(), [clearTimers]);

    const presence = getPresence(profile?.lastWatchedTime);
    const registrationDate = formatRegistrationDate(profile?.registrationDate);
    const friendsCount = Array.isArray(profile?.friendsList) ? profile.friendsList.length : null;
    const nickname = profile?.nickname || account?.nickname || 'Пользователь';
    const profileUrl = `/profile/${encodeURIComponent(nickname)}`;
    const backgroundUrl = profile?.id
        ? `${API_DOMAIN}/account/getProfileData/${profile.id}_profile_background.png`
        : null;

    const card = isOpen && canShow ? createPortal(
        <aside
            id={tooltipId}
            role="tooltip"
            className={`account-hover-card is-${position.placement}${profile?.subscribed ? ' is-premium' : ''}`}
            style={{ top: position.top, left: position.left }}
            onMouseEnter={() => window.clearTimeout(hideTimer.current)}
            onMouseLeave={closeCard}
            onClick={(event) => event.stopPropagation()}
        >
            <div className="account-hover-card__backdrop">
                {backgroundUrl && !backgroundFailed ? (
                    <img src={backgroundUrl} alt="" onError={() => setBackgroundFailed(true)} />
                ) : null}
            </div>
            <div className="account-hover-card__content">
                <div className="account-hover-card__identity">
                    <div className="account-hover-card__avatar">
                        <ProfilePic isBig={false} account={profile} />
                    </div>
                    <div className="account-hover-card__name-wrap">
                        <div className="account-hover-card__name">
                            <strong>{nickname}</strong>
                            {profile?.subscribed ? <img src={premiumBadge} alt="Premium" /> : null}
                        </div>
                        <span className={`account-hover-card__presence${presence.online ? ' is-online' : ''}`}>
                            <i />
                            {presence.label}
                        </span>
                    </div>
                </div>

                {profile?.mod || profile?.editor ? (
                    <div className="account-hover-card__roles">
                        {profile.mod ? <span>Модерация</span> : null}
                        {profile.editor ? <span>Редактор</span> : null}
                    </div>
                ) : null}

                <p className={`account-hover-card__description${profile?.description ? '' : ' is-empty'}`}>
                    {profile?.description || 'Пользователь пока ничего о себе не рассказал.'}
                </p>

                {friendsCount !== null || registrationDate ? (
                    <div className="account-hover-card__facts">
                        {friendsCount !== null ? <span><b>{friendsCount}</b> друзей</span> : null}
                        {registrationDate ? <span>На animix с <b>{registrationDate}</b></span> : null}
                    </div>
                ) : null}

                <Link className="account-hover-card__action" to={profileUrl} tabIndex={0}>
                    Открыть профиль <span aria-hidden="true">→</span>
                </Link>

                {isLoading ? <div className="account-hover-card__loading" aria-label="Загрузка профиля" /> : null}
            </div>
        </aside>,
        document.body,
    ) : null;

    return (
        <span
            ref={triggerRef}
            className="account-hover-card__trigger"
            aria-describedby={isOpen ? tooltipId : undefined}
            onMouseEnter={openCard}
            onMouseLeave={closeCard}
            onFocus={openCard}
            onBlur={closeCard}
        >
            {children}
            {card}
        </span>
    );
};

AccountHoverCard.propTypes = {
    account: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        nickname: PropTypes.string,
        banned: PropTypes.bool,
    }),
    children: PropTypes.node.isRequired,
};

export default AccountHoverCard;
